import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { bt } from '../i18n';
import { XIcon } from '../ui/icons';

/**
 * BottomSheet — a reusable DRAGGABLE bottom sheet, a faithful clone of the panel Shopify's theme editor
 * surfaces on tablet/mobile. Anchored to the bottom, full width, rounded TOP corners, a shadow and a
 * centred GRIP HANDLE bar. The handle is draggable (pointer + touch) to resize the sheet between three
 * SNAP POINTS — peek (just the header), half (~55vh) and full (~92vh); releasing snaps to the nearest.
 *
 * This is PURELY PRESENTATIONAL: it renders whatever children the shell hands it (the inspector, the
 * sections tree, the theme list, the app-embeds panel) without touching their logic, and it owns no
 * engine/document state — only its own open height. Smooth height transitions are gated behind
 * `prefers-reduced-motion: reduce` (the drag itself is always instant; only the post-release snap eases).
 *
 * The shell decides WHEN to mount the sheet and WHAT to put in it (per breakpoint + selection state); the
 * sheet only decides HOW TALL it is.
 */

export type SheetSnap = 'peek' | 'half' | 'full';

/** Heights for the three snaps. Peek is a fixed px (header only); half/full are viewport fractions. */
const PEEK_PX = 96;
const HALF_VH = 0.55;
const FULL_VH = 0.92;

function snapHeight(snap: SheetSnap): number {
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  switch (snap) {
    case 'peek':
      return PEEK_PX;
    case 'half':
      return Math.round(vh * HALF_VH);
    case 'full':
      return Math.round(vh * FULL_VH);
  }
}

/** Snap an arbitrary live drag height to the NEAREST of the three snap heights. */
function nearestSnap(height: number): SheetSnap {
  const candidates: SheetSnap[] = ['peek', 'half', 'full'];
  let best: SheetSnap = 'half';
  let bestDist = Infinity;
  for (const s of candidates) {
    const d = Math.abs(snapHeight(s) - height);
    if (d < bestDist) {
      bestDist = d;
      best = s;
    }
  }
  return best;
}

export interface BottomSheetProps {
  /** Sheet header title (e.g. the section name, or "Sections"). */
  title: ReactNode;
  /** Scrolling sheet body. */
  children: ReactNode;
  /** Optional "…" affordance node rendered before the close button (e.g. a section overflow menu). */
  more?: ReactNode;
  /** Close handler — wired by the shell to the right state reset (deselect / collapse the rail). */
  onClose: () => void;
  /** Accessible label for the close control (defaults to "Close"). */
  closeLabel?: string;
  /** Snap the sheet opens at. Defaults to `half`. */
  initialSnap?: SheetSnap;
  /** Accessible label for the whole sheet dialog. */
  ariaLabel?: string;
}

export function BottomSheet({
  title,
  children,
  more,
  onClose,
  closeLabel = bt('chrome.common.close', 'Close'),
  initialSnap = 'half',
  ariaLabel,
}: BottomSheetProps) {
  const [snap, setSnap] = useState<SheetSnap>(initialSnap);
  // `dragHeight` is non-null only WHILE dragging (live, un-snapped px height); otherwise the height comes
  // from the current snap so a viewport resize re-derives the right px. This keeps drag instant but the
  // resting state declarative.
  const [dragHeight, setDragHeight] = useState<number | null>(null);
  const dragRef = useRef<{ startY: number; startHeight: number; pointerId: number } | null>(null);

  const height = dragHeight ?? snapHeight(snap);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      // Only the primary button / touch / pen starts a drag.
      if (e.button !== undefined && e.button > 0) return;
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
      dragRef.current = {
        startY: e.clientY,
        startHeight: snapHeight(snap),
        pointerId: e.pointerId,
      };
      setDragHeight(snapHeight(snap));
    },
    [snap],
  );

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    // Dragging the handle UP (negative deltaY) grows the sheet; clamp to [peek, full].
    const delta = d.startY - e.clientY;
    const next = Math.min(snapHeight('full'), Math.max(PEEK_PX, d.startHeight + delta));
    setDragHeight(next);
  }, []);

  const endDrag = useCallback(
    (e: React.PointerEvent) => {
      const d = dragRef.current;
      if (!d || e.pointerId !== d.pointerId) return;
      (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
      const live = dragHeight ?? d.startHeight;
      dragRef.current = null;
      setDragHeight(null);
      setSnap(nearestSnap(live));
    },
    [dragHeight],
  );

  // Keyboard affordance on the handle: ↑/↓ step through snaps; Home/End jump to full/peek (a11y so the
  // sheet is resizable without a pointer). Esc anywhere closes.
  const onHandleKeyDown = useCallback((e: React.KeyboardEvent) => {
    const ORDER: SheetSnap[] = ['peek', 'half', 'full'];
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSnap((s) => ORDER[Math.min(ORDER.length - 1, ORDER.indexOf(s) + 1)] ?? 'full');
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSnap((s) => ORDER[Math.max(0, ORDER.indexOf(s) - 1)] ?? 'peek');
    } else if (e.key === 'Home') {
      e.preventDefault();
      setSnap('full');
    } else if (e.key === 'End') {
      e.preventDefault();
      setSnap('peek');
    }
  }, []);

  // Esc closes the sheet (Shopify parity). Scoped to the sheet's lifetime.
  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      if (ev.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="bld-sheet"
      role="dialog"
      aria-label={
        ariaLabel ?? (typeof title === 'string' ? title : bt('chrome.sheet.panel', 'Panel'))
      }
      data-dragging={dragHeight != null || undefined}
      data-snap={snap}
      style={{ height: `${height}px` }}
    >
      {/* Drag handle — pointer + touch resize, keyboard-resizable, exposes the snap state to AT. */}
      <div
        className="bld-sheet__handle"
        role="slider"
        tabIndex={0}
        aria-label={bt('chrome.sheet.resize', 'Resize panel')}
        aria-valuetext={snap}
        aria-valuemin={0}
        aria-valuemax={2}
        aria-valuenow={['peek', 'half', 'full'].indexOf(snap)}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={onHandleKeyDown}
      >
        <span className="bld-sheet__grip" aria-hidden="true" />
      </div>

      <div className="bld-sheet__head">
        <h2 className="bld-sheet__title">{title}</h2>
        <div className="bld-sheet__head-actions">
          {more}
          <button
            type="button"
            className="bld-sheet__close"
            aria-label={closeLabel}
            title={closeLabel}
            onClick={onClose}
          >
            <XIcon />
          </button>
        </div>
      </div>

      <div className="bld-sheet__body">{children}</div>
    </div>
  );
}
